import { useAnalysisStore } from '@modules/analysis/store/analysisStore';
import { CircularLoader } from '@modules/common/components/CircularLoader';
import { LazyLoadVirtualList } from '@modules/common/components/LazyLoadVirtualList';
import {
  getDashboardAuthor,
  getEmojiFromDashboard,
} from '@modules/dashboard/helpers';
import { EditableDashboardListItem } from '@modules/dashboard/types';
import {
  Box,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback } from 'react';

interface AddAnalysisToDashboardListProps {
  dashboards: EditableDashboardListItem[];
  isFetchingDashboards: boolean;
  onDashboardSelect: (dashboard: EditableDashboardListItem) => void;
  noDashboardsCreatedYet: boolean;
  searchQuery: string;
  fetchMoreEditableDashboards: () => Promise<void>;
  hasNextPage: boolean;
}

export const AddAnalysisToDashboardList = ({
  dashboards,
  isFetchingDashboards,
  onDashboardSelect,
  noDashboardsCreatedYet,
  searchQuery,
  fetchMoreEditableDashboards,
  hasNextPage,
}: AddAnalysisToDashboardListProps) => {
  const analysisID = useAnalysisStore(({ id }) => id);

  const dashboardListItemElement = useCallback(
    (dashboard: EditableDashboardListItem) => {
      const isAnalysisInDashboard = dashboard.analysisIDs?.includes(analysisID);

      const itemElement = (
        <MenuItem
          dense
          disabled={isAnalysisInDashboard}
          key={dashboard.id}
          onClick={() => onDashboardSelect(dashboard)}
        >
          <ListItemIcon
            sx={{
              color: 'unset',
            }}
          >
            {getEmojiFromDashboard(dashboard)}
          </ListItemIcon>
          <ListItemText
            primary={
              <Tooltip title={dashboard.title} disableInteractive>
                <Typography variant="body1" noWrap>
                  {dashboard.title}
                </Typography>
              </Tooltip>
            }
            secondary={
              <Typography variant="body2" color="text.secondary" noWrap>
                {getDashboardAuthor(dashboard.createdBy)}
              </Typography>
            }
          />
        </MenuItem>
      );

      if (isAnalysisInDashboard) {
        return (
          <Tooltip
            title="You can not add this data visualization to this
dashboard as it's already a part of it."
            placement="top"
            arrow
          >
            <Box>{itemElement}</Box>
          </Tooltip>
        );
      }

      return itemElement;
    },
    [analysisID, onDashboardSelect],
  );

  const renderContent = useCallback(() => {
    if (isFetchingDashboards && !dashboards.length) {
      return <CircularLoader size={24} />;
    }

    if (noDashboardsCreatedYet) {
      return (
        <Stack
          spacing={1}
          px={2}
          alignItems="center"
          justifyContent="center"
          height="100%"
        >
          <Box fontSize="3rem">📁</Box>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            <Box>No dashboards created or</Box>
            <Box>collaborated on yet.</Box>
          </Typography>
        </Stack>
      );
    }

    if (!dashboards.length && searchQuery.trim()) {
      return (
        <Stack px={2} alignItems="center" justifyContent="center" height="100%">
          <Typography variant="body2" color="text.secondary" px={2}>
            No matches found for "{searchQuery}"
          </Typography>
        </Stack>
      );
    }

    return (
      <LazyLoadVirtualList
        listHeight={260}
        listWidth={300}
        defaultListItemHeight={54}
        data={dashboards}
        loadMoreItems={fetchMoreEditableDashboards}
        renderRowComponent={dashboardListItemElement}
        hasNextPage={hasNextPage}
      />
    );
  }, [
    dashboardListItemElement,
    dashboards,
    isFetchingDashboards,
    noDashboardsCreatedYet,
    searchQuery,
    fetchMoreEditableDashboards,
    hasNextPage,
  ]);

  return (
    <Box height="260px" width="300px" overflow="auto">
      {renderContent()}
    </Box>
  );
};
